/**
 * Client mirror of the server's path-dependent `entity_invoke` permission
 * rule (ADR-012 Phase M4).
 *
 * The bridge op `entity_invoke` has no single static permission: integration
 * entities that declare `pluginInvocable.pluginPermission` in their catalog
 * config are gated by THAT permission (surface parity with their legacy
 * alias ops — `data_put` requires only `data:store`, so the identical
 * operation via `entity_invoke` must not require more), while the AI/text
 * path, capability-selector objects, and permission-less integration
 * entities keep requiring `entities:invoke`.
 *
 * The server's authoritative rule lives in
 * `services/api-service/src/routes/plugins/entity-invoke.ts`
 * (`entityInvokeRequiredPermission`). This map duplicates the config-declared
 * permissions to avoid pulling `@fias/entities` (and its server-only
 * transitive deps) into the client bundle — the same mirroring pattern as
 * `client-entity-registry.ts` for client-side entities. The architecture
 * test `client-integration-entity-permission-mirror.test.ts` enforces that
 * this map agrees exactly with the catalog (every non-client-side
 * integration config with a declared `pluginInvocable.pluginPermission`,
 * nothing more, nothing less), so a new or changed entity config cannot
 * silently drift from what the client host enforces.
 *
 * Client-side entities (`executionMode: 'client-side'`) are deliberately
 * absent: they dispatch via `client_entity_invoke`, whose per-entity
 * permission is resolved from the client-entity registry at request time.
 */
import type { PluginPermission } from '@fias/arche-sdk';

/**
 * Entity IDs for the migrated op-families (ADR-012 Phase L, Job 3). Duplicated
 * here because the client host cannot import `@fias/entities`; they must match
 * the object keys of `INTEGRATION_ENTITY_PERMISSIONS` below (pinned to the
 * catalog by `client-integration-entity-permission-mirror.test.ts`) AND the
 * SDK's `packages/arche-sdk/src/entity-ops.ts`. Consumed by the client host's
 * `entity_invoke` store handling and its verb-aware rate limiting.
 */
export const PLUGIN_STORAGE_ENTITY_ID = 'ent_intg_plugin_storage_00000000';
export const PLUGIN_DATASTORE_ENTITY_ID = 'ent_intg_plugin_datastore_000000';
export const PLUGIN_WORKSPACES_ENTITY_ID = 'ent_intg_plugin_workspaces_00000';
export const PLUGIN_STORE_ENTITY_ID = 'ent_intg_plugin_store_0000000000';
export const VAULT_USER_DOCUMENTS_ENTITY_ID = 'ent_intg_vault_documents_0000000';
export const VAULT_ARCHE_DOCUMENTS_ENTITY_ID = 'ent_intg_vault_arche_docs_000000';

export const INTEGRATION_ENTITY_PERMISSIONS: Readonly<Record<string, PluginPermission>> = {
  ent_intg_plugin_storage_00000000: 'storage:sandbox',
  ent_intg_plugin_datastore_000000: 'data:store',
  ent_intg_plugin_workspaces_00000: 'data:workspace',
  ent_intg_plugin_store_0000000000: 'store:purchase',
  ent_intg_web_search0000000000000: 'entities:web_search',
  // Consented user-document reads (the picker-granted subset) — the only
  // entity_invoke path that reaches data the plugin did not author.
  ent_intg_vault_documents_0000000: 'vault:user-documents:read',
  // Arche-OWNED document operations (byte read-back; in-place save, over the
  // bridge body or the presigned init/finalize lane) — gated by the
  // own-documents permission the plugin already holds for writing, never the
  // consent one. The save verbs additionally require `vault:documents:write`,
  // which the SERVER enforces per-verb
  // after the payload is parsed (this mirror is one permission per entity by
  // construction, same as community assets below).
  ent_intg_vault_arche_docs_000000: 'vault:documents:read',
  // Community assets. The READ permission is the entity-level gate; the
  // publish-side verbs additionally require `assets:community:publish`, which
  // the SERVER enforces per-verb after the payload is parsed (this mirror is
  // one permission per entity by construction).
  ent_intg_community_assets_000000: 'assets:community:read',
  // Document Sign — consented reads of envelopes the calling arche itself
  // started via a `sign_document` handoff. Read-only by construction: the
  // server payload schema admits only the two consented verbs, so there is no
  // write permission to mirror here.
  ent_intg_document_sign_000000000: 'docsign:envelopes:read',
  // Company email (ADR-023): an app sending AS the Company it is used in. The
  // server additionally requires the Company's send-as authorization.
  ent_intg_company_email_send_0000: 'email:company:send',
  // Connections (ADR-029 §2): list the accounts the acting workspace lets this
  // app use, and release its own access. Read + withdraw only.
  ent_intg_connections_00000000000: 'connections:use',
  // X and LinkedIn (ADR-029 §3): act AS an account the acting workspace let
  // this app use. Posting additionally needs a person's approval per post (the
  // outward guard), which no permission grants.
  ent_intg_twitter_000000000000000: 'connections:use',
  ent_intg_linkedin_00000000000000: 'connections:use',
  // Scheduled invocations (ADR-029 §5): schedule one approved call for later.
  ent_intg_scheduled_invocations00: 'schedule:invoke',
  // LitCraft Hub: the read-only LitCraft surface (the player's own data and the
  // community boards). Its own permission, never LitCraft Player's game link.
  ent_intg_litcraft_hub_0000000000: 'litcraft:hub',
};

/**
 * The permission a plugin must hold to make this `entity_invoke` call.
 * Never returns undefined — every call is gated by exactly one of the
 * entity-declared permission or `entities:invoke`, never neither.
 */
export function entityInvokeRequiredPermission(entityId: unknown): PluginPermission {
  if (typeof entityId === 'string') {
    const declared = INTEGRATION_ENTITY_PERMISSIONS[entityId];
    if (declared) return declared;
  }
  return 'entities:invoke';
}
